import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IcdCode } from 'src/app/core/services/app-data.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { ageFromDob, sexFromPatient, toOnsetDate } from 'src/app/shared/util/icd-coding-context.util';
import { orderIcdCodes } from 'src/app/shared/util/icd-chip-state.util';
import { IcdCodeSelectorComponent } from '../icd-code-selector/icd-code-selector.component';

export interface IcdCodeEditDialogData {
  patient: any;
}

/**
 * Edits a patient's ICD-10 codes from the patient file, without the round trip through the full
 * edit form.
 *
 * ── WHY A NEW COMPONENT RATHER THAN REUSING THE AI-REQUEST ONE ───────────────────────
 *
 * `#diagnosisIcdDialog` in `patient-ai-request-detail` looks like this dialog and is not: it is
 * an inline `TemplateRef` bound to that component's `diagnosisIcdForm` and its `diagnosis2..24`
 * fields, and it exists to GATE a save — "you cannot generate this until the record has a
 * diagnosis". Lifting it would have dragged the diagnosis fields and that save flow onto the
 * patient file. The reverse migration is the sane direction and is tracked as a follow-up.
 *
 * ── THE SAVE CONTRACT ────────────────────────────────────────────────────────────────
 *
 * `PUT /patients/:id` already does the whole job (`patientController.js:1033`): a code WITH an
 * `id` is an update routed through `applyIcdUpdates`, one without is a create, and one absent
 * from the array is soft-deleted with an audit row. So the array must be sent whole, WITH ids.
 *
 * ⚠ `initialCodes` therefore hands the picker the server rows untouched. `icd-code-selector`
 * keeps them by reference (`icdChips = initialCodes.slice()`) and throws its codebook enrichment
 * away rather than writing it back, so `id`, `source`, `flaggedForRemoval` and `episodeId`
 * survive a round trip. Mapping the codes to a "clean" shape on the way in would strip the ids,
 * and every save would then soft-delete and re-create every code on the record — destroying
 * `connectSubmissionStatus`, orphaning claim lines, and burying the audit trail.
 *
 * ⚠ Z00 IS SHOWN, NOT FILTERED. The AI-request dialog filters it because it is capturing a fresh
 * diagnosis. This is the opposite case: `requiresICDValidation` exists to say "this record was
 * auto-coded Z00, fix it", so hiding the one code the clinician came here to delete would defeat
 * the feature.
 */
@Component({
  selector: 'app-icd-code-edit-dialog',
  templateUrl: './icd-code-edit-dialog.component.html',
  styleUrls: ['./icd-code-edit-dialog.component.scss'],
})
export class IcdCodeEditDialogComponent {
  @ViewChild('selector') selector?: IcdCodeSelectorComponent;

  /** The server rows, ordered but otherwise untouched — see the ids warning above. */
  initialCodes: IcdCode[] = [];
  selectedCodes: IcdCode[] = [];

  saving = false;
  errorMessage: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<IcdCodeEditDialogComponent>,
    private patientService: PatientService,
    @Inject(MAT_DIALOG_DATA) public data: IcdCodeEditDialogData,
  ) {
    this.initialCodes = orderIcdCodes(this.data?.patient?.icdCodes || []) as IcdCode[];
    this.selectedCodes = this.initialCodes.slice();
  }

  get patientSex(): string | null { return sexFromPatient(this.data?.patient); }
  get patientAge(): number | null { return ageFromDob(this.data?.patient?.dob); }
  get defaultOnsetDate(): string | null { return toOnsetDate(this.data?.patient?.admissionDate); }

  onCodesChanged(codes: IcdCode[]): void {
    this.selectedCodes = codes || [];
    this.errorMessage = null;
  }

  /**
   * The same floor the full edit form enforces (`patient-detail.component.ts:1108`): a patient
   * that is not an invite needs at least one code. Enforced here too so the shortcut cannot be
   * used to reach a state the long way round refuses.
   */
  get canSave(): boolean {
    return !this.saving && this.selectedCodes.length > 0;
  }

  save(): void {
    if (!this.canSave) return;

    // Without this a missing id produces `PUT /patients/undefined`, which the server answers
    // with a flat "Patient not found" — indistinguishable from the patient genuinely not being
    // in the database the API is connected to. Two very different faults, one message. Fail
    // here instead, where the cause is unambiguous.
    if (!this.data?.patient?.id) {
      this.errorMessage = 'This patient record has no id, so the codes cannot be saved. Reload the file and try again.';
      return;
    }

    this.saving = true;
    this.errorMessage = null;

    // ONLY `icdCodes`. The update path treats every field it receives as an intended change, so
    // a payload carrying the rest of the patient would re-submit stale values for anything that
    // moved since this dialog opened. `Array.isArray(icdCodes)` is the server's gate
    // (`patientController.js:1033`) and every other field is left alone when absent.
    //
    // Array position IS the ordering — index 0 is the primary diagnosis — and the server writes
    // it to `sequence` from exactly this order. Sent as-is, ids included.
    this.patientService.updatePatient(this.data.patient.id, { icdCodes: this.selectedCodes }).subscribe({
      next: () => {
        this.saving = false;
        // `changed: true` is the caller's signal to refetch. The chips, the ordering and the
        // suggestion badge all move together or the card contradicts itself.
        this.dialogRef.close({ changed: true });
      },
      error: (error: any) => {
        this.saving = false;
        // Held open on failure. Closing would lose the clinician's edit and leave the card
        // showing the old codes with no indication the save never landed.
        this.errorMessage = error?.error?.message || 'The codes could not be saved. Please try again.';
      },
    });
  }

  cancel(): void {
    this.dialogRef.close({ changed: false });
  }
}
